import React from 'react';
import {css, styled} from 'styled-components';
import {colors} from '../../styles/colors';
import {useFormatMsg} from 'utils/useFormatMsg';
import DefaultText from './DefaultText';
import TextareaAutosize from 'react-textarea-autosize';
import {minmax} from 'utils/minmax';

/**
 * 텍스트 영역 Props 타입
 */
type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement>;
/**
 * 스타일 타입
 * 텍스트 영역의 스타일을 정의합니다.
 */
type Style = Omit<
  NonNullable<TextareaProps['style']>,
  'maxHeight' | 'minHeight'
> & {
  height?: number;
};

/**
 * 라벨 텍스트 영역 컴포넌트의 Props 인터페이스
 * @property {any} labelText - 라벨 텍스트
 * @property {any} value - 입력값
 * @property {any} [setValue] - 값 변경 핸들러
 * @property {string} [placeholder] - 플레이스홀더 텍스트
 * @property {boolean} [isColumn] - 세로 배치 여부
 * @property {React.CSSProperties} [labelStyle] - 라벨 스타일
 * @property {React.CSSProperties} [labelInputContainerStyle] - 라벨 입력 컨테이너 스타일
 * @property {React.CSSProperties} [textAreaStyle] - 텍스트 영역 스타일
 * @property {boolean} [readOnly] - 읽기 전용 여부
 * @property {() => void} [onClickInput] - 입력 필드 클릭 핸들러
 * @property {number} [maxRows] - 최대 행 수
 * @property {string} [rightImg] - 우측 이미지 경로
 * @property {() => void} [onClickRightImg] - 우측 이미지 클릭 핸들러
 * @property {Style} [rowTextAreaStyle] - 행 텍스트 영역 스타일
 * @property {React.CSSProperties} [inputWrapStyle] - 입력 래퍼 스타일
 * @property {boolean} [disabled] - 비활성화 여부
 */
interface ILabelInputProps {
  labelText: any;
  value: any;
  setValue?: any;
  placeholder?: string;
  isColumn?: boolean;
  labelStyle?: React.CSSProperties;
  labelInputContainerStyle?: React.CSSProperties;
  textAreaStyle?: React.CSSProperties;
  readOnly?: boolean;
  onClickInput?: () => void;
  maxRows?: number;
  rightImg?: string;
  onClickRightImg?: () => void;
  rowTextAreaStyle?: Style;
  inputWrapStyle?: React.CSSProperties;
  disabled?: boolean;
}

/**
 * 라벨 텍스트 영역 컴포넌트
 * 라벨과 함께 여러 줄 텍스트를 입력할 수 있는 컴포넌트입니다.
 * @param {ILabelInputProps} props - 라벨 텍스트 영역 컴포넌트의 props
 * @returns {JSX.Element} 라벨 텍스트 영역 컴포넌트
 */
const LabelTextarea = ({
  labelText,
  value,
  setValue,
  placeholder,
  isColumn,
  labelStyle,
  labelInputContainerStyle,
  textAreaStyle,
  readOnly,
  onClickInput,
  maxRows,
  rightImg,
  onClickRightImg,
  rowTextAreaStyle,
  inputWrapStyle,
  disabled,
}: ILabelInputProps) => {
  const label = useFormatMsg(labelText) || labelText;
  const placeholderText =
    (placeholder && useFormatMsg(placeholder)) || placeholder;

  return (
    <LabelInputWrap $isColumn={isColumn} style={labelInputContainerStyle}>
      <Label htmlFor={label + value} style={labelStyle} $isColumn={isColumn}>
        {label}
      </Label>

      {maxRows ? (
        <LineTextAreaContainer
          style={{cursor: readOnly ? 'default' : 'text', ...textAreaStyle}}>
          <TextareaAutosizeComponent
            cacheMeasurements
            value={value}
            placeholder={placeholderText}
            onChange={(e: any) => {
              setValue(e.target.value);
            }}
            readOnly={readOnly}
            onClick={onClickInput}
            maxRows={maxRows}
            style={{
              cursor: readOnly ? 'default' : 'text',
              width: rightImg ? '90%' : '100%',
              ...rowTextAreaStyle,
            }}
          />
          {rightImg && <RightImg src={rightImg} onClick={onClickRightImg} />}
        </LineTextAreaContainer>
      ) : (
        <InputWrap style={inputWrapStyle}>
          <TextArea
            value={value}
            placeholder={placeholderText}
            onChange={(e: any) => setValue(e.target.value)}
            style={{cursor: readOnly ? 'default' : 'text', ...textAreaStyle}}
            readOnly={readOnly}
            disabled={disabled}
          />
        </InputWrap>
      )}
    </LabelInputWrap>
  );
};

export default LabelTextarea;

const LabelInputWrap = styled.div<{$isColumn?: boolean}>`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  position: relative;

  ${(props: any) =>
    props.$isColumn &&
    css`
      flex-direction: column;
      height: auto;
    `}
`;

const Label = styled.label<{$isColumn?: boolean}>`
  font-size: ${minmax(20)};
  font-weight: 400;
  width: 20%;
  padding-right: 2%;
  height: max(40px, min(3.5vw, 60px));
  display: flex;
  justify-content: start;
  align-items: center;
  word-break: break-all;
  ${(props: any) =>
    props.$isColumn &&
    css`
      width: 100%;
    `}
`;

export const TextArea = styled.textarea`
  width: 100%;
  height: ${minmax(280)};
  padding: ${minmax(16)} ${minmax(20)};
  border-radius: ${minmax(10)};
  outline: none;
  border: 1px solid ${colors.borderGray};
  resize: none;
  padding: ${minmax(16)} ${minmax(20)};
  caret-color: ${colors.primary};
  font-family: 'Noto Sans KR', sans-serif;
  font-size: ${minmax(20)};
  font-weight: 400;
  color: ${colors.black};

  // 스크롤바 삭제
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera*/
  }

  &::placeholder {
    color: ${colors.lightGrey};
  }
`;

const TextareaAutosizeComponent = styled(TextareaAutosize)`
  width: 100%;
  border-radius: ${minmax(10)};
  outline: none;
  border: 1px solid ${colors.borderGray};
  resize: none;
  font-family: 'Noto Sans KR', sans-serif;
  font-size: ${minmax(20)};
  font-weight: 400;
  color: ${colors.black};
  border: 0;

  // 스크롤바 삭제
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera*/
  }

  &::placeholder {
    color: ${colors.lightGrey};
  }
`;

const LineTextAreaContainer = styled.div`
  width: 100%;
  min-height: ${minmax(60)};
  padding: ${minmax(20)} ${minmax(20)};
  border-radius: ${minmax(10)};
  border: 1px solid ${colors.borderGray};
  background-color: white;
  display: flex;
  align-items: center;
`;

const RightImg = styled.img`
  width: ${minmax(60)};
  position: absolute;
  top: 50%;
  right: 0;
  transform: translate(0, -50%);
  cursor: pointer;
`;

const InputWrap = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
`;
